import { useEffect, useMemo, useState } from "react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import { useToast } from "../../../hooks/use-toast";
import { Role } from "../../../types";
import {
  useRolesForOps,
  useSetUserRoles,
  useUserRoles,
} from "../hooks/use-role-queries";

interface UserRolesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string | null;
  userName?: string;
  // The user's ops (gestor is filtered out — it's not a client tenant).
  opsList: string[];
  onSuccess?: () => void;
}

export function UserRolesDialog({
  open,
  onOpenChange,
  userId,
  userName,
  opsList,
  onSuccess,
}: UserRolesDialogProps) {
  const { toast } = useToast();
  const clientOps = useMemo(
    () => opsList.filter((o) => o !== "gestor"),
    [opsList]
  );

  const { data: roles = [], isLoading: rolesLoading } =
    useRolesForOps(clientOps);
  const { data: assignedData, isLoading: assignedLoading } = useUserRoles(
    open ? userId ?? undefined : undefined
  );
  const setUserRoles = useSetUserRoles();

  const [selected, setSelected] = useState<string[]>([]);

  // Seed the selection once the user's roles load. Depend on the query-data reference
  // (stable across renders in react-query) — NOT a fresh `[]` default, which would
  // change every render and re-run setSelected endlessly (React error #185).
  useEffect(() => {
    if (open && assignedData) {
      setSelected(assignedData);
    }
  }, [open, assignedData]);

  // Group roles by ops for display.
  const rolesByOps = useMemo(() => {
    const map = new Map<string, Role[]>();
    for (const role of roles) {
      if (!map.has(role.ops)) map.set(role.ops, []);
      map.get(role.ops)!.push(role);
    }
    return map;
  }, [roles]);

  const toggle = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );
  };

  const handleSave = async () => {
    if (!userId) return;
    try {
      await setUserRoles.mutateAsync({ userId, roleIds: selected });
      toast({ title: "Sucesso", description: "Funções atualizadas!" });
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error?.message || "Erro ao atualizar funções",
        variant: "destructive",
      });
    }
  };

  const loading = rolesLoading || assignedLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Funções de {userName || "usuário"}</DialogTitle>
          <DialogDescription>
            Atribua quantas funções desejar, dentro das operações do usuário.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {loading && (
            <span className="text-sm text-muted-foreground">Carregando...</span>
          )}
          {!loading && clientOps.length === 0 && (
            <span className="text-sm text-muted-foreground">
              O usuário não pertence a nenhuma operação cliente.
            </span>
          )}
          {!loading && clientOps.length > 0 && roles.length === 0 && (
            <span className="text-sm text-muted-foreground">
              Nenhuma função cadastrada para as operações do usuário.
            </span>
          )}
          {Array.from(rolesByOps.entries()).map(([ops, opsRoles]) => (
            <div key={ops} className="grid gap-2">
              <Label className="text-xs font-semibold uppercase text-muted-foreground">
                {ops}
              </Label>
              <div className="flex flex-wrap gap-2">
                {opsRoles.map((role) => {
                  const isSelected = selected.includes(role.id);
                  return (
                    <Badge
                      key={role.id}
                      role="checkbox"
                      aria-checked={isSelected}
                      aria-label={role.name}
                      tabIndex={0}
                      variant={isSelected ? "default" : "outline"}
                      className="cursor-pointer select-none text-sm px-3 py-1.5"
                      onClick={() => toggle(role.id)}
                      onKeyDown={(e) => {
                        if (e.key === " " || e.key === "Enter") {
                          e.preventDefault();
                          toggle(role.id);
                        }
                      }}
                    >
                      {role.name}
                    </Badge>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={setUserRoles.isPending || !userId}
          >
            {setUserRoles.isPending ? "Salvando..." : "Salvar Funções"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
